import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Users } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  extractDiscountInfo,
  getReservationGuestName,
  getReservationTimeValue,
  getStatusMeta,
  isReservationOnDay,
} from './utils';

const TIME_START = 8;
const TIME_END = 24;
const PIXELS_PER_MINUTE = 2.2;

const TIME_SLOTS = Array.from({ length: TIME_END - TIME_START }, (_, index) =>
  `${String(TIME_START + index).padStart(2, '0')}:00`,
);

function getSlotLeft(timeString) {
  const [hours, minutes] = String(timeString || '00:00').split(':').map((value) => Number(value || 0));
  return (((hours * 60) + minutes) - (TIME_START * 60)) * PIXELS_PER_MINUTE;
}

function getReservationPositionStyle(reservation) {
  const [hours, minutes] = String(getReservationTimeValue(reservation) || '00:00')
    .split(':')
    .map((value) => Number(value || 0));
  const startMinutes = (hours * 60) + minutes;
  const dayStartMinutes = TIME_START * 60;
  const offsetMinutes = Math.max(0, startMinutes - dayStartMinutes);
  const duration = Number(reservation.duration_minutes || 90);

  return {
    left: `${offsetMinutes * PIXELS_PER_MINUTE}px`,
    width: `${Math.max(36, duration * PIXELS_PER_MINUTE)}px`,
  };
}

export default function ReservationTimelineView({
  reservations = [],
  tables = [],
  displayDate,
  isLoading,
  onSelectReservation,
}) {
  const containerRef = useRef(null);
  const [nowPosition, setNowPosition] = useState(null);

  const dayReservations = useMemo(
    () => reservations.filter((reservation) => isReservationOnDay(reservation, displayDate)),
    [reservations, displayDate],
  );

  const sections = useMemo(() => {
    const grouped = {};
    [...tables]
      .sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''), undefined, { numeric: true }))
      .forEach((table) => {
        const sectionName = table?.section?.name || table?.area?.name || 'Main Dining';
        if (!grouped[sectionName]) grouped[sectionName] = [];
        grouped[sectionName].push(table);
      });
    return grouped;
  }, [tables]);

  useEffect(() => {
    const updateIndicator = () => {
      const now = new Date();
      if (!displayDate || !isReservationOnDay({ reservation_date: now.toISOString() }, displayDate)) {
        setNowPosition(null);
        return;
      }

      const currentMinutes = (now.getHours() * 60) + now.getMinutes();
      const startMinutes = TIME_START * 60;
      if (currentMinutes >= startMinutes && currentMinutes <= (TIME_END * 60)) {
        setNowPosition((currentMinutes - startMinutes) * PIXELS_PER_MINUTE);
      } else {
        setNowPosition(null);
      }
    };

    updateIndicator();
    const intervalId = window.setInterval(updateIndicator, 60000);
    return () => window.clearInterval(intervalId);
  }, [displayDate]);

  if (isLoading && !tables.length) {
    return (
      <div className="flex h-full items-center justify-center rounded-md border bg-background">
        <span className="text-sm text-slate-500">Timeline wird geladen…</span>
      </div>
    );
  }

  if (!tables.length) {
    return (
      <div className="m-4 flex h-full flex-col items-center justify-center rounded-md border border-dashed bg-muted/10 text-muted-foreground">
        <AlertCircle className="mb-2 h-10 w-10 opacity-20" />
        <p>Keine Reservierungstische konfiguriert.</p>
      </div>
    );
  }

  const unassignedReservations = dayReservations.filter(
    (reservation) => !reservation.table_id && !['cancelled', 'no_show', 'completed'].includes(String(reservation.status || '')),
  );

  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-md border bg-background shadow-sm">
      {unassignedReservations.length ? (
        <div className="z-30 flex flex-wrap items-center gap-3 border-b border-amber-200 bg-amber-50 px-3 py-1.5">
          <div className="flex items-center gap-2 text-xs font-medium text-amber-800">
            <AlertCircle className="h-3.5 w-3.5" />
            <span>{unassignedReservations.length} nicht zugewiesen</span>
          </div>
          <div className="flex max-w-full gap-2 overflow-x-auto pb-1">
            {unassignedReservations.map((reservation) => (
              <button
                key={reservation.id}
                type="button"
                className="flex whitespace-nowrap rounded border border-amber-200 bg-white px-2 py-0.5 text-[10px] text-amber-900 shadow-sm transition-shadow hover:shadow-md"
                onClick={() => onSelectReservation?.(reservation)}
              >
                <span className="font-bold">{getReservationGuestName(reservation)}</span>
                <span className="ml-2 flex items-center text-amber-700/70">
                  <Users className="mr-0.5 h-3 w-3" />
                  {reservation.party_size || 0}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="relative flex-1 overflow-hidden">
        <div ref={containerRef} className="relative h-full overflow-auto bg-background">
          <div className="min-w-max pb-10" style={{ width: `${((TIME_END - TIME_START) * 60 * PIXELS_PER_MINUTE) + 180}px` }}>
            <div className="sticky top-0 z-40 flex h-8 border-b bg-background shadow-sm">
              <div className="sticky left-0 z-50 flex w-44 shrink-0 items-center border-r bg-background pl-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Tisch
              </div>
              <div className="relative flex h-full flex-1 items-end bg-background">
                {TIME_SLOTS.map((time) => (
                  <div
                    key={time}
                    className="absolute bottom-0 h-3 border-l border-muted/50 pl-1 text-[9px] font-medium text-muted-foreground"
                    style={{ left: `${getSlotLeft(time)}px` }}
                  >
                    {time}
                  </div>
                ))}
                {nowPosition !== null ? (
                  <div
                    className="absolute bottom-0 z-50 h-3 -translate-x-1/2 border-l-2 border-red-500"
                    style={{ left: `${nowPosition}px` }}
                  >
                    <div className="-ml-[3.5px] -mt-[2px] h-1.5 w-1.5 rounded-full bg-red-500" />
                  </div>
                ) : null}
              </div>
            </div>

            <div className="pointer-events-none absolute inset-0 left-44 top-8 z-0 h-full">
              {TIME_SLOTS.map((time) => (
                <div
                  key={`grid-${time}`}
                  className="absolute h-full border-l border-dashed border-border/40"
                  style={{ left: `${getSlotLeft(time)}px` }}
                />
              ))}
              {nowPosition !== null ? (
                <div className="absolute top-0 bottom-0 border-l border-red-500/20" style={{ left: `${nowPosition}px` }} />
              ) : null}
            </div>

            {Object.entries(sections).map(([sectionName, sectionTables]) => (
              <div key={sectionName}>
                <div className="sticky left-0 z-10 w-full border-y bg-muted/20 px-4 py-0.5 text-[10px] font-bold text-foreground/70">
                  {sectionName}
                </div>

                {sectionTables.map((table) => {
                  const tableReservations = dayReservations.filter(
                    (reservation) => String(reservation.table_id || '') === String(table.id || ''),
                  );

                  return (
                    <div key={table.id} className="group relative z-10 flex h-12 border-b border-border/40 transition-colors hover:bg-muted/5">
                      <div className="sticky left-0 z-20 flex w-44 shrink-0 flex-col justify-center border-r border-r-border bg-background/95 p-1.5 backdrop-blur-[2px] group-hover:bg-muted/5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <span className="block truncate text-xs font-semibold text-foreground" title={table.name}>
                              {table.name}
                            </span>
                            <span className="text-[9px] text-muted-foreground">{table.shape || 'Standard'}</span>
                          </div>
                          <span className="flex shrink-0 items-center rounded bg-secondary/50 px-1 py-0.5 font-mono text-[9px] text-secondary-foreground">
                            <Users className="mr-0.5 h-2.5 w-2.5" />
                            {table.min_seats || 2}-{table.max_seats || table.capacity || 4}
                          </span>
                        </div>
                      </div>

                      <div className="relative flex-1">
                        {tableReservations.map((reservation) => {
                          const status = getStatusMeta(reservation.status);
                          const discountInfo = extractDiscountInfo(reservation);

                          return (
                            <button
                              key={reservation.id}
                              type="button"
                              className={cn(
                                'absolute bottom-1 top-1 z-20 flex flex-col justify-center overflow-hidden rounded border px-1.5 text-left text-xs shadow-sm transition-all hover:z-30 hover:scale-[1.02] hover:shadow-md',
                                status.pillClass,
                              )}
                              style={getReservationPositionStyle(reservation)}
                              onClick={() => onSelectReservation?.(reservation)}
                            >
                              <div className="flex w-full items-center gap-1 text-[10px] font-bold leading-tight">
                                <span className="truncate">{getReservationGuestName(reservation)}</span>
                                {discountInfo.hasDiscount ? (
                                  <div className="ml-auto shrink-0 rounded-sm bg-black px-1 text-[8px] font-bold text-white">
                                    -{discountInfo.percentage}%
                                  </div>
                                ) : null}
                              </div>
                              {Number.parseInt(getReservationPositionStyle(reservation).width, 10) > 40 ? (
                                <div className="mt-0.5 flex items-center justify-between text-[9px] opacity-80">
                                  <span className="flex items-center">
                                    <Users className="mr-0.5 h-2.5 w-2.5" />
                                    {reservation.party_size || 0}
                                  </span>
                                  <span>{getReservationTimeValue(reservation)}</span>
                                </div>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
